import { t } from "i18next";
import type { CustomFieldValue, ICustomField } from "store/customFieldsStore";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";
import type { SubsetOf } from "types";

export type TFieldsDetails = {
    key: string;
    name: string;
    valueType: CustomFieldValue | undefined;
    native: boolean;
    display?: boolean;
    id?: number;
    uuid?: string;
};

export type FieldData = {
    id?: number;
    key: string;
    label: string;
    fields: (TFieldsDetails | ICustomField)[];
    showOnForm: boolean;
};

// I left the valueType property in case it is ever needed in the future but for now it's not used for native fields.
export const titleFormFields = (
    publisherType: EPortalType.BOOK | undefined,
    conditions: Record<string, boolean>,
): Record<string, FieldData> => {
    return {
        generalInfo: {
            key: "generalInfo",
            label: t("NewTitle.GeneralInformation"),
            showOnForm: true,
            fields: [
                {
                    key: "ean13",
                    name: t("NewTitle.ISBN"),
                    valueType: "string",
                    native: true,
                },
                {
                    key: "name",
                    name: t("NewTitle.Title"),
                    valueType: "string",
                    native: true,
                },
                {
                    key: "artworkNature",
                    name: t("NewPublication.ArtworkNature"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "releaseDate",
                    name: t("NewTitle.ReleaseDate"),
                    valueType: "date",
                    native: true,
                },
                {
                    key: "avatarUrl",
                    name: t("NewTitle.ArtworkImage"),
                    valueType: "string",
                    native: true,
                },
            ],
        },
        prices: {
            key: "prices",
            label: t("NewTitle.SpecificPrices"),
            showOnForm: false,
            fields: [
                {
                    key: "price",
                    name: t(`NewTitle.Price`),
                    valueType: "date",
                    native: true,
                },
                {
                    key: "currency",
                    name: t("NewTitle.Currency"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "overridenChannelsData",
                    name: t("Titles.PricePerChannel"),
                    valueType: undefined,
                    native: true,
                },
            ],
        },
        closingInfo: {
            key: "closingInfo",
            label: t("NewTitle.LastFinYearInformation"),
            showOnForm: false,
            fields: [
                {
                    key: "initialStockData",
                    name: t("NewTitle.QuantityStock"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "cumulativeSales",
                    name: t("NewTitle.CumulativeSalesInfo"),
                    valueType: undefined,
                    native: true,
                },
            ],
        },
        otherInfo: {
            key: "otherInfo",
            label: t("NewTitle.OtherInformation"),
            showOnForm: true,
            fields: [
                {
                    key: "characteristics",
                    name: t("Titles.Specification"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "administrativeInfo",
                    name: t("NewTitle.AdminInfo"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "associatedBrand",
                    name: t("NewPublication.Brand"),
                    valueType: undefined,
                    native: true,
                    display: conditions.usesMultipleBrands,
                },
                {
                    key: "summary",
                    name: t("NewTitle.Summary"),
                    valueType: "longstring",
                    native: true,
                },
                {
                    key: "notes",
                    name: t("NewTitle.Notes"),
                    valueType: "longstring",
                    native: true,
                },
            ],
        },
    };
};

export const beneficiaryFormFields = (
    publisherType: SubsetOf<TPortalType, "press" | "audiovisual" | "book"> | undefined,
    conditions: Record<string, boolean>,
): Record<string, FieldData> => {
    return {
        personalInfo: {
            key: "personalInfo",
            label: t("NewBeneficiary.PersonalInformation"),
            showOnForm: true,
            fields: [
                {
                    key: "name",
                    name: t("NewBeneficiary.Name"),
                    valueType: "string",
                    native: true,
                },
                {
                    key: "surname",
                    name: t("NewBeneficiary.Surname"),
                    valueType: "string",
                    native: true,
                },
                {
                    key: "pseudonym",
                    name: t("ImportData.BenPseudonym"),
                    valueType: "string",
                    native: true,
                    display: publisherType === EPortalType.BOOK,
                },
                {
                    key: "phoneNumber",
                    name: t("NewBeneficiary.PhoneNumber"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "birthDate",
                    name: t("NewBeneficiary.BirthDate"),
                    valueType: "date",
                    native: true,
                },
                {
                    key: "email",
                    name: t("NewBeneficiary.Email"),
                    valueType: "string",
                    native: true,
                },
                {
                    key: "beneficiaryLanguage",
                    name: t("NewBeneficiary.Language"),
                    valueType: "string",
                    native: true,
                },
            ],
        },
        address: {
            key: "address",
            label: t("Beneficiars.Address"),
            showOnForm: false,
            fields: [
                {
                    key: "street",
                    name: t("NewBeneficiary.StreetNumber"),
                    valueType: "date",
                    native: true,
                },
                {
                    key: "zipCode",
                    name: t("NewBeneficiary.ZIPCode"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "city",
                    name: t("NewBeneficiary.City"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "country",
                    name: t("NewBeneficiary.Country"),
                    valueType: undefined,
                    native: true,
                },
            ],
        },
        profile: {
            key: "profile",
            label: t("NewBeneficiary.Profile"),
            showOnForm: false,
            fields: [
                {
                    key: "socialProfileType",
                    name: t("NewBeneficiary.SocialProfile"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "vat",
                    name: t("NewBeneficiary.VAT"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "activityType",
                    name: t("NewBeneficiary.ActivityType"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "socialSecurityNumber",
                    name: t("Beneficiars.SecurityNumber"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "siretNumber",
                    name: t("Beneficiars.Siret"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "beneficiaryCode",
                    name: t("Beneficiars.Code"),
                    valueType: undefined,
                    native: true,
                },
            ],
        },
        accounting: {
            key: "accounting",
            label: t("NewBeneficiary.AccountingPayments"),
            showOnForm: true,
            fields: [
                {
                    key: "acceptedPayments",
                    name: t("NewBeneficiary.PaymentsMethods"),
                    valueType: undefined,
                    native: true,
                },
                {
                    key: "accountingInfo",
                    name: t("Beneficiars.AccountingInfo"),
                    valueType: undefined,
                    native: true,
                },
            ],
        },
    };
};

export const publicationFormFields = (
    publisherType: "press" | "audiovisual" | undefined,
    conditions: Record<string, boolean>,
): Record<string, FieldData> => {
    return {
        generalInfo: {
            key: "generalInfo",
            label: t(`NewPublication.NewPublication_${publisherType}`),
            showOnForm: true,
            fields: [
                {
                    key: "publicationName",
                    name: t(`NewPublication.PublicationName_${publisherType}`),
                    valueType: "string",
                    native: true,
                },
                {
                    key: "issnCode",
                    name: conditions.isPreferredIdentifierConcat
                        ? t("NewPublication.ISSNCode")
                        : t("NewPublication.UniqueCode"),
                    valueType: "string",
                    native: true,
                },
                {
                    key: "parutionCode",
                    name: t("NewPublication.ParutionCode"),
                    valueType: undefined,
                    native: true,
                    display: conditions.isPreferredIdentifierConcat,
                },
                {
                    key: "analysisCode",
                    name: t("ImportData.TitleAnalysisCode"),
                    valueType: "string",
                    native: true,
                },
                {
                    key: "artworkNature",
                    name: t("NewPublication.ArtworkNature"),
                    valueType: "string",
                    native: true,
                },
            ],
        },
        dates: {
            key: "dates",
            label: conditions.isFolioBased ? t("NewPublication.FolioAndDates") : t("NewPublication.Dates"),
            showOnForm: true,
            fields: [
                {
                    key: "pricePerFolio",
                    name: t("NewPublication.PricePerFolio"),
                    valueType: "string",
                    native: true,
                    display: conditions.isFolioBased || conditions.usesBothCalculationMethods,
                },
                {
                    key: "deliveryDate",
                    name: t("NewPublication.DeliveryDate"),
                    valueType: "date",
                    native: true,
                },
                {
                    key: "releaseDate",
                    name: t("NewPublication.ReleaseDate"),
                    valueType: "date",
                    native: true,
                },
            ],
        },
    };
};

/** Sections of the term form. Custom fields are attached to one of these locations. */
export const termFormFields = (): Record<string, FieldData> => {
    return {
        parties: {
            key: "parties",
            label: t("ConnectTitle.Step1Title"),
            showOnForm: true,
            fields: [
                { key: "beneficiary", name: t("ConnectTitle.LinkedBeneficiary"), valueType: "string", native: true },
                { key: "reference", name: t("ConnectTitle.TermReference"), valueType: "string", native: true },
            ],
        },
        channels: {
            key: "channels",
            label: t("ConnectTitle.Step2Title"),
            showOnForm: true,
            fields: [{ key: "channels", name: t("ConnectTitle.Step2Title"), valueType: undefined, native: true }],
        },
        settings: {
            key: "settings",
            label: t("ConnectTitle.Step3Title"),
            showOnForm: true,
            fields: [
                { key: "endDate", name: t("ConnectTitle.Date"), valueType: "date", native: true },
                { key: "initialProvision", name: t("ConnectTitle.Provision"), valueType: "number", native: true },
            ],
        },
    };
};
